import { Box } from '@mui/material';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import {
  DeleteButton,
  DeleteButtonProps,
} from '@/components/form/baseInputs/Button/DeleteButton';

import {
  FormModalActionsProvider,
  useFormModalActionsContext,
} from './FormModalActions.provider';

export type FormModalCancelButtonProps = Partial<ButtonProps>;

export const FormModalCancelButton = ({
  size = 'sm',
  variant = 'secondary',
  children = 'Cancel',
  disabled,
  ...props
}: FormModalCancelButtonProps) => {
  const formCtx = useFormModalActionsContext();

  // Use "Close" as text for the cancel button in view only mode
  if (formCtx?.viewOnly) {
    children = 'Close';
  }

  return (
    <Button
      disabled={disabled ?? formCtx?.disabled}
      size={size}
      variant={variant}
      {...props}
    >
      {children}
    </Button>
  );
};

export type FormModalSaveButtonProps = Partial<ButtonProps>;
export const FormModalSaveButton = ({
  size = 'sm',
  variant = 'primary',
  children = 'Save',
  loading,
  disabled,
  ...props
}: FormModalSaveButtonProps) => {
  const formCtx = useFormModalActionsContext();

  // Don't show the save button in view only mode
  if (formCtx?.viewOnly) {
    return null;
  }

  return (
    <Button
      loading={loading || formCtx?.isSubmitting}
      disabled={disabled || formCtx?.disabled}
      size={size}
      variant={variant}
      {...props}
    >
      {children}
    </Button>
  );
};

export type FormModalDeleteButtonProps = DeleteButtonProps;
export const FormModalDeleteButton = ({
  loading,
  disabled,
  ...props
}: FormModalDeleteButtonProps) => {
  const formCtx = useFormModalActionsContext();

  // Don't show the delete button in view only mode
  if (formCtx?.viewOnly) {
    return null;
  }

  return (
    <>
      <DeleteButton
        loading={loading || formCtx?.isDeleting}
        disabled={disabled || formCtx?.disabled}
        {...props}
      />
      <Box flexGrow={1} />
    </>
  );
};

export type FormModalDestructiveButtonProps = Partial<ButtonProps>;
export const FormModalDestructiveButton = ({
  size = 'sm',
  variant = 'destructive-secondary',
  children = 'Destroy',
  loading,
  disabled,
  ...props
}: FormModalDestructiveButtonProps) => {
  const formCtx = useFormModalActionsContext();
  return (
    <>
      <Button
        loading={loading || formCtx?.isSubmitting}
        disabled={disabled || formCtx?.disabled}
        size={size}
        variant={variant}
        {...props}
      >
        {children}
      </Button>
      <Box flexGrow={1} />
    </>
  );
};

export const FormModalActions = {
  Provider: FormModalActionsProvider,
  CancelButton: FormModalCancelButton,
  SaveButton: FormModalSaveButton,
  DeleteButton: FormModalDeleteButton,
  DestructiveButton: FormModalDestructiveButton,
};
